/**
 * Lit une image depuis le presse-papiers (après Print Screen ou Win+Shift+S).
 * @returns {Promise<Blob|null>}
 */
export async function getImageFromClipboard() {
  try {
    const clipboardItems = await navigator.clipboard.read()
    for (const item of clipboardItems) {
      const imageType = item.types.find((t) => t.startsWith('image/'))
      if (imageType) {
        return await item.getType(imageType)
      }
    }
  } catch (e) {
    if (e.name === 'NotAllowedError') {
      throw new Error('Accès au presse-papiers refusé. Autorisez-le dans votre navigateur.')
    }
    throw e
  }
  return null
}

/**
 * Extrait un blob image depuis un événement paste (Ctrl+V).
 * @param {ClipboardEvent} event
 * @returns {Blob|null}
 */
export function getImageFromPasteEvent(event) {
  const items = event.clipboardData?.items
  if (!items) return null
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      return item.getAsFile()
    }
  }
  return null
}

/**
 * Redimensionne une image (File ou Blob) en JPEG 320×180 et retourne un data URL base64.
 * Stocké directement dans Firestore (pas besoin de Firebase Storage).
 * Taille typique : 15-25 Ko en base64.
 * @param {File|Blob} file
 * @param {number} width
 * @param {number} height
 * @returns {Promise<string>} data URL (data:image/jpeg;base64,...)
 */
export function resizeImageToDataUrl(file, width = 320, height = 180) {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')

      const scale = Math.max(width / img.width, height / img.height)
      const sw = width / scale
      const sh = height / scale
      const sx = (img.width - sw) / 2
      const sy = (img.height - sh) / 2
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height)

      const dataUrl = canvas.toDataURL('image/jpeg', 0.82)
      URL.revokeObjectURL(img.src)
      resolve(dataUrl)
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error('Impossible de charger l\'image'))
    }
    img.src = URL.createObjectURL(file)
  })
}
